// skills/smithing/smithingUI.js
// Smithing UI rendering and interaction

import { smithingSkill } from './smithing.js';
import { BARS, ARROW_HEADS, SWORDS, DIRKS, GREATSWORDS, MELEE_ARMOR, BULWARKS, MELEE_TIERS, getEquipmentForTier, TOOLS } from './smithingData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS, GAME_CONFIG } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { getItemName, getItemIcon } from '../../data/items/index.js';
import { formatWithCommas, formatNumber } from '../../utils/format.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { buildEquipmentStatsTooltip } from '../../ui/equipmentTooltip.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus, getTalentLootBonus, getTalentFlatBonus } from '../../systems/talentTree/talentTree.js';
import { getSkillIcon } from '../../core/skillRegistry.js';

// Current tab state
let currentTab = 'bars';
let currentMeleeTier = 1;

// Track loot multiplier to detect corruption changes
let lastLootMultiplier = 1;

/**
 * Render compact skill header with integrated tool upgrade
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.SMITHING);
    const xpPerHour = getXPPerHour(SKILLS.SMITHING);

    // Calculate display values
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let levelDisplay, progressPercent, progressText;

    if (isMax && virtualEnabled) {
        levelDisplay = `${skill.level} (${skill.virtualLevel})`;
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        levelDisplay = `${skill.level} (Max)`;
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        levelDisplay = skill.level;
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    // Get tool info
    const tools = Object.values(TOOLS);
    const highestOwned = gameState.getHighestPurchasedTool(SKILLS.SMITHING, tools);
    const activeTool = highestOwned || tools[0];
    const activeMultiplier = activeTool?.speedMultiplier || 1.0;
    const goldCount = gameState.getGold();

    // Find next upgrade
    const allToolsSorted = [...tools].sort((a, b) => a.level - b.level);
    const nextUpgrade = allToolsSorted.find(tool =>
        tool.price > 0 && !gameState.hasPurchasedTool(SKILLS.SMITHING, tool.id)
    );

    // Build upgrade HTML
    let upgradeHtml = '';
    if (nextUpgrade) {
        const isLocked = skill.level < nextUpgrade.level;
        const canAfford = goldCount >= nextUpgrade.price;

        if (isLocked) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextUpgrade.name} (${nextUpgrade.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextUpgrade.price}">${getItemIcon('coins')} ${formatNumber(nextUpgrade.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextUpgrade.level}</span>
                </span>
            `;
        } else {
            upgradeHtml = `
                <span class="compact-upgrade ${canAfford ? 'available' : ''}"
                      onclick="window.smithingUI.upgradeTool('${nextUpgrade.id}')">
                    → ${nextUpgrade.name} (${nextUpgrade.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextUpgrade.price}">${getItemIcon('coins')} ${formatNumber(nextUpgrade.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ All hammers</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.SMITHING)}</span>
                    <span class="compact-name">Smithing</span>
                    ${renderSkillInfo(SKILLS.SMITHING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.SMITHING)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Hammer:</span>
                    <span class="tool-current">${activeTool.name} (${activeMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render tab navigation with tier tabs
 */
function renderTabs() {
    const skill = gameState.getSkill(SKILLS.SMITHING);

    // Build tier tabs with bar icons
    const tierTabs = Object.entries(MELEE_TIERS).map(([tier, info]) => {
        const tierNum = parseInt(tier);
        const isLocked = skill.level < info.level;
        const isActive = currentTab === 'melee' && currentMeleeTier === tierNum;
        const lockIcon = isLocked ? '🔒 ' : '';
        const barIcon = info.barId ? getItemIcon(info.barId) : '';

        return `
            <button class="skill-tab tier-tab ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}"
                    onclick="window.smithingUI.switchMeleeTier(${tierNum})"
                    style="--tier-color: ${info.color}"
                    ${isLocked ? `title="Requires level ${info.level}"` : ''}>
                ${lockIcon}<span class="tab-bar-icon">${barIcon}</span>${info.name}
            </button>
        `;
    }).join('');

    // Use pyrestone icons as silhouette for tab buttons
    const barsTabIcon = getItemIcon('pyrestone_bar');
    const arrowheadTabIcon = getItemIcon('pyrestone_arrowheads');

    return `
        <div class="skill-tabs smithing-tabs">
            <button class="skill-tab ${currentTab === 'bars' ? 'active' : ''}"
                    onclick="window.smithingUI.switchTab('bars')">
                <span class="tab-bar-icon silhouette">${barsTabIcon}</span>Bars
            </button>
            <button class="skill-tab ${currentTab === 'ammunition' ? 'active' : ''}"
                    onclick="window.smithingUI.switchTab('ammunition')">
                <span class="tab-bar-icon silhouette">${arrowheadTabIcon}</span>Arrowheads
            </button>
            <div class="tier-tabs-separator"></div>
            ${tierTabs}
        </div>
    `;
}

/**
 * Render a single bar card
 */
function renderBarCard(bar, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= bar.level;
    const isActive = currentAction?.type === 'smithing' && currentAction?.bar?.id === bar.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    // getEffectiveSpeedMultiplier includes: tool, talent tool, mastery, admin, talent speed, stacking
    const speedMultiplier = smithingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.SMITHING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.SMITHING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = bar.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.SMITHING);
    let effectiveXP = bar.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Get loot multipliers from corruption and talents (Tempered Output)
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.SMITHING);
    const talentLootBonus = getTalentLootBonus(SKILLS.SMITHING);

    // Check if player has materials
    const hasInput = gameState.getItemCount(bar.inputItem) >= bar.inputQuantity;
    const hasSecondary = !bar.secondaryItem || gameState.getItemCount(bar.secondaryItem) >= bar.secondaryQuantity;
    const hasMaterials = hasInput && hasSecondary;

    // Get item info
    const inputIcon = getItemIcon(bar.inputItem);
    const inputName = getItemName(bar.inputItem);
    const inputCount = gameState.getItemCount(bar.inputItem);

    const outputDrop = bar.drops[0];
    const outputIcon = getItemIcon(outputDrop.itemId);
    const outputName = getItemName(outputDrop.itemId);
    const baseQty = outputDrop.quantity;
    const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;

    const cardClasses = [
        'smithing-card',
        isUnlocked ? '' : 'locked',
        isActive ? 'active' : '',
        !hasMaterials && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    // Build input requirements HTML
    let inputHtml = `
        <div class="material-row ${hasInput ? '' : 'missing'}">
            <span class="material-icon">${inputIcon}</span>
            <span class="material-name">${inputName}</span>
            <span class="material-count">${inputCount}/${bar.inputQuantity}</span>
        </div>
    `;

    if (bar.secondaryItem) {
        const secIcon = getItemIcon(bar.secondaryItem);
        const secName = getItemName(bar.secondaryItem);
        const secCount = gameState.getItemCount(bar.secondaryItem);
        inputHtml += `
            <div class="material-row ${hasSecondary ? '' : 'missing'}">
                <span class="material-icon">${secIcon}</span>
                <span class="material-name">${secName}</span>
                <span class="material-count">${secCount}/${bar.secondaryQuantity}</span>
            </div>
        `;
    }

    // Output display with loot multiplier
    const qtyDisplay = isBoosted
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    // Use output bar icon for header (sprite or fallback)
    const barHeaderIcon = outputIcon || '🔥';

    return `
        <div class="${cardClasses}"
             data-bar-id="${bar.id}"
             style="--bar-color: ${bar.color}">
            <div class="smithing-card-header">
                <span class="smithing-card-level">${isUnlocked ? `Lvl ${bar.level}` : `Req: ${bar.level}`}</span>
                <span class="smithing-card-icon">${barHeaderIcon}</span>
                <span class="smithing-card-name">${bar.name}</span>
            </div>

            <div class="smithing-card-info">
                <div class="smithing-stat">
                    <span class="stat-icon smithing-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="smithing-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="smithing-materials">
                <div class="materials-label">Input:</div>
                ${inputHtml}
            </div>

            <div class="smithing-output">
                <div class="output-label">Output:</div>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="smithing-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.smithingUI.toggleSmithing('${bar.id}')">
                        ${isActive ? 'Stop' : 'Smith'}
                    </button>
                ` : `
                    <span class="locked-text">Locked</span>
                `}
            </div>

            <div class="smithing-progress">
                <div class="smithing-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render a single arrowhead card
 */
function renderArrowheadCard(arrowhead, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= arrowhead.level;
    const isActive = currentAction?.type === 'smithing' && currentAction?.arrowhead?.id === arrowhead.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    const speedMultiplier = smithingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.SMITHING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.SMITHING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = arrowhead.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.SMITHING);
    let effectiveXP = arrowhead.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Get loot multipliers from corruption and talents
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.SMITHING);
    const talentLootBonus = getTalentLootBonus(SKILLS.SMITHING);
    const arrowheadOutput = getTalentFlatBonus(SKILLS.SMITHING, 'arrowheadOutput');
    const fletcherSupply = getTalentFlatBonus(SKILLS.SMITHING, 'fletcherSupply');

    // Check if player has materials
    const hasInput = gameState.getItemCount(arrowhead.inputItem) >= arrowhead.inputQuantity;

    // Get item info
    const inputIcon = getItemIcon(arrowhead.inputItem);
    const inputName = getItemName(arrowhead.inputItem);
    const inputCount = gameState.getItemCount(arrowhead.inputItem);

    const outputDrop = arrowhead.drops[0];
    const outputIcon = getItemIcon(outputDrop.itemId);
    const outputName = getItemName(outputDrop.itemId);
    const baseQty = outputDrop.quantity;
    // Arrowheads get Tempered Output + Arrowsmith + Fletcher's Supply bonuses
    let arrowOutputMult = 1;
    if (talentLootBonus > 0) arrowOutputMult *= (1 + talentLootBonus);
    if (arrowheadOutput > 0) arrowOutputMult *= (1 + arrowheadOutput);
    if (fletcherSupply > 0) arrowOutputMult *= (1 + fletcherSupply);
    const adjustedQty = Math.max(1, Math.floor(baseQty * arrowOutputMult));
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;

    const cardClasses = [
        'smithing-card',
        isUnlocked ? '' : 'locked',
        isActive ? 'active' : '',
        !hasInput && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    // Output display with loot multiplier
    const qtyDisplay = isBoosted
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    return `
        <div class="${cardClasses}"
             data-arrowhead-id="${arrowhead.id}"
             style="--bar-color: ${arrowhead.color}">
            <div class="smithing-card-header">
                <span class="smithing-card-level">${isUnlocked ? `Lvl ${arrowhead.level}` : `Req: ${arrowhead.level}`}</span>
                <span class="smithing-card-icon">${outputIcon}</span>
                <span class="smithing-card-name">${arrowhead.name}</span>
            </div>

            <div class="smithing-card-info">
                <div class="smithing-stat">
                    <span class="stat-icon smithing-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="smithing-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="smithing-materials">
                <div class="materials-label">Input:</div>
                <div class="material-row ${hasInput ? '' : 'missing'}">
                    <span class="material-icon">${inputIcon}</span>
                    <span class="material-name">${inputName}</span>
                    <span class="material-count">${inputCount}/${arrowhead.inputQuantity}</span>
                </div>
            </div>

            <div class="smithing-output">
                <div class="output-label">Output:</div>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="smithing-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.smithingUI.toggleSmithingArrowheads('${arrowhead.id}')">
                        ${isActive ? 'Stop' : 'Smith'}
                    </button>
                ` : `
                    <span class="locked-text">Locked</span>
                `}
            </div>

            <div class="smithing-progress">
                <div class="smithing-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render bars tab content
 */
function renderBarsTab() {
    const skill = gameState.getSkill(SKILLS.SMITHING);
    const currentAction = gameState.getCurrentAction();
    const bars = Object.values(BARS);

    return `
        <div class="smithing-grid">
            ${bars.map(bar => renderBarCard(bar, skill.level, currentAction)).join('')}
        </div>
    `;
}

/**
 * Render ammunition tab content
 */
function renderAmmunitionTab() {
    const skill = gameState.getSkill(SKILLS.SMITHING);
    const currentAction = gameState.getCurrentAction();
    const arrowheads = Object.values(ARROW_HEADS);

    return `
        <div class="smithing-grid">
            ${arrowheads.map(ah => renderArrowheadCard(ah, skill.level, currentAction)).join('')}
        </div>
    `;
}

/**
 * Render a single sword card
 */
function renderSwordCard(sword, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= sword.level;
    const isActive = currentAction?.type === 'smithing' && currentAction?.sword?.id === sword.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    const speedMultiplier = smithingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.SMITHING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.SMITHING);

    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = sword.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent + equipment expertise
    const talentXPBonus = getTalentXPBonus(SKILLS.SMITHING);
    const equipXPBonus = getTalentFlatBonus(SKILLS.SMITHING, 'equipmentXPBonus');
    let effectiveXP = sword.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));
    if (equipXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + equipXPBonus));

    // Input materials
    const inputCount = gameState.getItemCount(sword.inputItem);
    const hasInput = inputCount >= sword.inputQuantity;
    const inputIcon = getItemIcon(sword.inputItem);
    const inputName = getItemName(sword.inputItem);

    // Output
    const outputIcon = getItemIcon(sword.drops[0]?.itemId) || '⚔️';
    const outputName = sword.name;

    // Build class list
    const cardClasses = [
        'smithing-card',
        isUnlocked ? '' : 'locked',
        isActive ? 'active' : '',
        !hasInput && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-sword-id="${sword.id}"
             style="--bar-color: ${sword.color}">
            <div class="smithing-card-header">
                <span class="smithing-card-level">${isUnlocked ? `Lvl ${sword.level}` : `Req: ${sword.level}`}</span>
                <span class="smithing-card-icon">${getItemIcon(sword.drops[0]?.itemId) || '⚔️'}</span>
                <span class="smithing-card-name">${sword.name}</span>
            </div>

            <div class="smithing-card-info">
                <div class="smithing-stat">
                    <span class="stat-icon smithing-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="smithing-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="smithing-materials">
                <div class="materials-label">Input:</div>
                <div class="material-row ${hasInput ? '' : 'missing'}">
                    <span class="material-icon">${inputIcon}</span>
                    <span class="material-name">${inputName}</span>
                    <span class="material-count">${inputCount}/${sword.inputQuantity}</span>
                </div>
            </div>

            <div class="smithing-output">
                <div class="output-label">Output:</div>
                <div class="output-row output-hoverable" data-tooltip="${buildEquipmentStatsTooltip(sword.drops[0].itemId)}">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    <span class="output-qty">x1</span>
                </div>
            </div>

            <div class="smithing-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.smithingUI.toggleSmithingSword('${sword.id}')">
                        ${isActive ? 'Stop' : 'Smith'}
                    </button>
                ` : `
                    <span class="locked-text">Locked</span>
                `}
            </div>

            <div class="smithing-progress">
                <div class="smithing-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render swords tab content (legacy - kept for backwards compatibility)
 */
function renderSwordsTab() {
    const skill = gameState.getSkill(SKILLS.SMITHING);
    const currentAction = gameState.getCurrentAction();
    const swords = Object.values(SWORDS);

    return `
        <div class="smithing-grid">
            <h3>Forge Swords</h3>
            <div class="smithing-grid">
                ${swords.map(sword => renderSwordCard(sword, skill.level, currentAction)).join('')}
            </div>
        </div>
    `;
}

/**
 * Get icon for equipment type
 */
function getEquipmentIcon(type, slot) {
    const icons = {
        dirk: '🗡️',
        sword: '⚔️',
        greatsword: '🔱',
        armor: {
            helm: '🪖',
            gloves: '🧤',
            boots: '👢',
            shoulders: '🛡️',
            chestplate: '🦺',
            legguards: '👖'
        }
    };

    if (type === 'armor') {
        return icons.armor[slot] || '🛡️';
    }
    return icons[type] || '⚔️';
}

/**
 * Render an equipment card (dirk, sword, greatsword, or armor)
 */
function renderEquipmentCard(item, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= item.level;

    // Determine if this item is currently being smithed
    let isActive = false;
    if (currentAction?.type === 'smithing') {
        if (item.type === 'dirk' && currentAction.dirk?.id === item.id) isActive = true;
        if (item.type === 'sword' && currentAction.sword?.id === item.id) isActive = true;
        if (item.type === 'greatsword' && currentAction.greatsword?.id === item.id) isActive = true;
        if (item.type === 'armor' && currentAction.meleeArmor?.id === item.id) isActive = true;
        if (item.type === 'bulwark' && currentAction.bulwark?.id === item.id) isActive = true;
    }

    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time and XP with buffs
    const speedMultiplier = smithingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.SMITHING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.SMITHING);

    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = item.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent + equipment expertise
    const talentXPBonus = getTalentXPBonus(SKILLS.SMITHING);
    const equipXPBonus = getTalentFlatBonus(SKILLS.SMITHING, 'equipmentXPBonus');
    let effectiveXP = item.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));
    if (equipXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + equipXPBonus));

    // Input materials
    const inputCount = gameState.getItemCount(item.inputItem);
    const hasInput = inputCount >= item.inputQuantity;
    const inputIcon = getItemIcon(item.inputItem);
    const inputName = getItemName(item.inputItem);

    // Get icon based on type - use sprite if available, fall back to emoji
    const itemIcon = getItemIcon(item.id);
    const icon = itemIcon || getEquipmentIcon(item.type, item.slot);

    // Determine toggle function based on type
    let toggleFn;
    switch (item.type) {
        case 'dirk': toggleFn = `toggleSmithingDirk('${item.id}')`; break;
        case 'sword': toggleFn = `toggleSmithingSword('${item.id}')`; break;
        case 'greatsword': toggleFn = `toggleSmithingGreatsword('${item.id}')`; break;
        case 'armor': toggleFn = `toggleSmithingMeleeArmor('${item.id}')`; break;
        case 'bulwark': toggleFn = `toggleSmithingBulwark('${item.id}')`; break;
    }

    // Build class list
    const cardClasses = [
        'smithing-card',
        'equipment-card',
        isUnlocked ? '' : 'locked',
        isActive ? 'active' : '',
        !hasInput && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    // Type label for display
    const typeLabels = {
        dirk: 'Dirk',
        sword: 'Sword',
        greatsword: '2H Greatsword',
        armor: item.slot ? item.slot.charAt(0).toUpperCase() + item.slot.slice(1) : 'Armor',
        bulwark: 'Bulwark'
    };
    const typeLabel = typeLabels[item.type] || item.type;

    return `
        <div class="${cardClasses}"
             data-equipment-id="${item.id}"
             data-equipment-type="${item.type}"
             style="--bar-color: ${item.color}">
            <div class="smithing-card-header">
                <span class="smithing-card-level">${isUnlocked ? `Lvl ${item.level}` : `Req: ${item.level}`}</span>
                <span class="smithing-card-icon">${icon}</span>
                <span class="smithing-card-name">${item.name}</span>
            </div>

            <div class="smithing-card-info">
                <div class="smithing-stat">
                    <span class="stat-icon smithing-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="smithing-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="smithing-materials">
                <div class="materials-label">Input:</div>
                <div class="material-row ${hasInput ? '' : 'missing'}">
                    <span class="material-icon">${inputIcon}</span>
                    <span class="material-name">${inputName}</span>
                    <span class="material-count">${inputCount}/${item.inputQuantity}</span>
                </div>
            </div>

            <div class="smithing-output">
                <div class="output-label">Output:</div>
                <div class="output-row output-hoverable" data-tooltip="${buildEquipmentStatsTooltip(item.id)}">
                    <span class="output-icon">${icon}</span>
                    <span class="output-name">${item.name}</span>
                    <span class="output-qty">x1</span>
                </div>
            </div>

            <div class="smithing-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.smithingUI.${toggleFn}">
                        ${isActive ? 'Stop' : 'Smith'}
                    </button>
                ` : `
                    <span class="locked-text">Locked</span>
                `}
            </div>

            <div class="smithing-progress">
                <div class="smithing-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render melee tier content (equipment for a specific tier)
 */
function renderMeleeTierContent(tier) {
    const skill = gameState.getSkill(SKILLS.SMITHING);
    const currentAction = gameState.getCurrentAction();
    const equipment = getEquipmentForTier(tier);

    return `
        <div class="smithing-grid">
            ${equipment.map(item => renderEquipmentCard(item, skill.level, currentAction)).join('')}
        </div>
    `;
}

export function renderSmithingUI(params = {}) {
    // Handle tab from route params
    if (params.tab) {
        currentTab = params.tab;
    }

    const container = document.getElementById('skill-content');

    let tabContent;
    switch (currentTab) {
        case 'ammunition':
            tabContent = renderAmmunitionTab();
            break;
        case 'melee':
            tabContent = renderMeleeTierContent(currentMeleeTier);
            break;
        default:
            tabContent = renderBarsTab();
    }

    container.innerHTML = `
    <div class="smithing-interface">
      ${renderCompactHeader()}

      <div class="smithing-content">
        ${renderTabs()}

        <div class="tab-content">
            ${tabContent}
        </div>
      </div>
    </div>
  `;
}

export function switchTab(tab) {
    currentTab = tab;
    renderSmithingUI();
}

export function switchMeleeTier(tier) {
    currentTab = 'melee';
    currentMeleeTier = tier;
    renderSmithingUI();
}

export function toggleSmithing(barId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.bar?.id === barId) {
        smithingSkill.stopSmithing();
    } else {
        smithingSkill.startSmithing(barId);
    }

    renderSmithingUI();
}

export function toggleSmithingArrowheads(arrowheadId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.arrowhead?.id === arrowheadId) {
        smithingSkill.stopSmithingArrowheads();
    } else {
        smithingSkill.startSmithingArrowheads(arrowheadId);
    }

    renderSmithingUI();
}

export function toggleSmithingSword(swordId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.sword?.id === swordId) {
        smithingSkill.stopSmithingSword();
    } else {
        smithingSkill.startSmithingSword(swordId);
    }

    renderSmithingUI();
}

export function toggleSmithingDirk(dirkId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.dirk?.id === dirkId) {
        smithingSkill.stopSmithingDirk();
    } else {
        smithingSkill.startSmithingDirk(dirkId);
    }

    renderSmithingUI();
}

export function toggleSmithingGreatsword(greatswordId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.greatsword?.id === greatswordId) {
        smithingSkill.stopSmithingGreatsword();
    } else {
        smithingSkill.startSmithingGreatsword(greatswordId);
    }

    renderSmithingUI();
}

export function toggleSmithingMeleeArmor(armorId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.meleeArmor?.id === armorId) {
        smithingSkill.stopSmithingMeleeArmor();
    } else {
        smithingSkill.startSmithingMeleeArmor(armorId);
    }

    renderSmithingUI();
}

export function toggleSmithingBulwark(bulwarkId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.bulwark?.id === bulwarkId) {
        smithingSkill.stopSmithingBulwark();
    } else {
        smithingSkill.startSmithingBulwark(bulwarkId);
    }

    renderSmithingUI();
}

export function upgradeTool(toolId) {
    const result = smithingSkill.upgradeTool(toolId);

    if (result.success) {
        renderSmithingUI();
    } else {
        logger.warn('Tool upgrade failed:', result.reason);
    }
}

export function updateSmithingUI() {
    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.SMITHING);
    const xpPerHour = getXPPerHour(SKILLS.SMITHING);

    // Update XP total
    const xpTotalEl = document.querySelector('.smithing-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    // Update XP rate
    const xpRateEl = document.querySelector('.smithing-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.smithing-interface');

    // Update progress bar
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let progressPercent, progressText;
    if (isMax && virtualEnabled) {
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    const progressFill = document.querySelector('.smithing-interface .compact-progress-fill');
    if (progressFill) progressFill.style.width = `${progressPercent}%`;

    const progressPercentEl = document.querySelector('.smithing-interface .compact-progress-percent');
    if (progressPercentEl) progressPercentEl.textContent = `${Math.floor(progressPercent)}%`;

    const progressTextEl = document.querySelector('.smithing-interface .compact-progress-text');
    if (progressTextEl) progressTextEl.textContent = `${progressText} XP`;

    // Check if loot multiplier changed (corruption started/ended)
    const currentLootMultiplier = getCorruptionLootMultiplier(SKILLS.SMITHING);
    if (currentLootMultiplier !== lastLootMultiplier) {
        lastLootMultiplier = currentLootMultiplier;
        if (!isTalentTreeOpen()) {
            renderSmithingUI();
        }
        return;
    }

    // Update material counts on all visible cards
    updateMaterialCounts();

    // Update action progress if smithing is active
    const action = gameState.getCurrentAction();
    if (!action || action.type !== 'smithing') return;

    // Find active card based on action type
    let activeCard = null;
    if (action.bar) {
        activeCard = document.querySelector(`.smithing-card[data-bar-id="${action.bar.id}"]`);
    } else if (action.arrowhead) {
        activeCard = document.querySelector(`.smithing-card[data-arrowhead-id="${action.arrowhead.id}"]`);
    } else if (action.sword) {
        activeCard = document.querySelector(`.smithing-card[data-equipment-id="${action.sword.id}"]`);
    } else if (action.dirk) {
        activeCard = document.querySelector(`.smithing-card[data-equipment-id="${action.dirk.id}"]`);
    } else if (action.greatsword) {
        activeCard = document.querySelector(`.smithing-card[data-equipment-id="${action.greatsword.id}"]`);
    } else if (action.meleeArmor) {
        activeCard = document.querySelector(`.smithing-card[data-equipment-id="${action.meleeArmor.id}"]`);
    }

    if (activeCard) {
        const progressFill = activeCard.querySelector('.smithing-progress-fill');
        if (progressFill) {
            progressFill.style.width = `${action.progress * 100}%`;
        }
    }
}

/**
 * Update material counts on all visible cards
 */
function updateMaterialCounts() {
    const cards = document.querySelectorAll('.smithing-interface .smithing-card');

    cards.forEach(card => {
        const barId = card.dataset.barId;
        const arrowheadId = card.dataset.arrowheadId;
        const equipmentId = card.dataset.equipmentId;
        const equipmentType = card.dataset.equipmentType;

        if (barId) {
            // Bar card
            const bar = Object.values(BARS).find(b => b.id === barId);
            if (!bar) return;

            const inputCount = gameState.getItemCount(bar.inputItem);
            const hasInput = inputCount >= bar.inputQuantity;

            // Update primary input
            const materialRows = card.querySelectorAll('.material-row');
            if (materialRows[0]) {
                const countEl = materialRows[0].querySelector('.material-count');
                if (countEl) countEl.textContent = `${inputCount}/${bar.inputQuantity}`;
                materialRows[0].classList.toggle('missing', !hasInput);
            }

            // Update secondary input if exists
            let hasSecondary = true;
            if (bar.secondaryItem && materialRows[1]) {
                const secCount = gameState.getItemCount(bar.secondaryItem);
                hasSecondary = secCount >= bar.secondaryQuantity;
                const secCountEl = materialRows[1].querySelector('.material-count');
                if (secCountEl) secCountEl.textContent = `${secCount}/${bar.secondaryQuantity}`;
                materialRows[1].classList.toggle('missing', !hasSecondary);
            }

            card.classList.toggle('no-materials', (!hasInput || !hasSecondary) && !card.classList.contains('locked'));
        } else if (arrowheadId) {
            // Arrowhead card
            const arrowhead = Object.values(ARROW_HEADS).find(a => a.id === arrowheadId);
            if (!arrowhead) return;

            const inputCount = gameState.getItemCount(arrowhead.inputItem);
            const hasMaterials = inputCount >= arrowhead.inputQuantity;

            const countEl = card.querySelector('.material-count');
            if (countEl) countEl.textContent = `${inputCount}/${arrowhead.inputQuantity}`;

            const materialRow = card.querySelector('.material-row');
            if (materialRow) materialRow.classList.toggle('missing', !hasMaterials);

            card.classList.toggle('no-materials', !hasMaterials && !card.classList.contains('locked'));
        } else if (equipmentId && equipmentType) {
            // Equipment card (dirk, sword, greatsword, armor)
            let item;
            switch (equipmentType) {
                case 'dirk':
                    item = Object.values(DIRKS).find(d => d.id === equipmentId);
                    break;
                case 'sword':
                    item = Object.values(SWORDS).find(s => s.id === equipmentId);
                    break;
                case 'greatsword':
                    item = Object.values(GREATSWORDS).find(g => g.id === equipmentId);
                    break;
                case 'armor':
                    // Melee armor is organized by tier, search all tiers
                    for (const tier of Object.keys(MELEE_ARMOR)) {
                        item = Object.values(MELEE_ARMOR[tier]).find(a => a.id === equipmentId);
                        if (item) break;
                    }
                    break;
            }
            if (!item) return;

            const inputCount = gameState.getItemCount(item.inputItem);
            const hasMaterials = inputCount >= item.inputQuantity;

            const countEl = card.querySelector('.material-count');
            if (countEl) countEl.textContent = `${inputCount}/${item.inputQuantity}`;

            const materialRow = card.querySelector('.material-row');
            if (materialRow) materialRow.classList.toggle('missing', !hasMaterials);

            card.classList.toggle('no-materials', !hasMaterials && !card.classList.contains('locked'));
        }
    });
}

export function initSmithingUI() {
    // Helper to handle completion callbacks with level-up logging and re-render
    const handleCompletion = (xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Smithing level ${xpResult.newLevel}!`);
        }

        // Only re-render if smithing page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.SMITHING && !isTalentTreeOpen()) {
            // Skip full re-render at high speeds to prevent button flickering
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderSmithingUI();
            }
        }
    };

    // Setup bar smithing completion callback
    smithingSkill.onSmithingComplete = (bar, xpResult) => {
        handleCompletion(xpResult);
    };

    // Setup arrowhead smithing completion callback
    smithingSkill.onSmithingArrowheadsComplete = (arrowhead, xpResult) => {
        handleCompletion(xpResult);
    };

    // Setup sword smithing completion callback
    smithingSkill.onSmithingSwordComplete = (sword, xpResult) => {
        handleCompletion(xpResult);
    };

    // Setup dirk smithing completion callback
    smithingSkill.onSmithingDirkComplete = (dirk, xpResult) => {
        handleCompletion(xpResult);
    };

    // Setup greatsword smithing completion callback
    smithingSkill.onSmithingGreatswordComplete = (greatsword, xpResult) => {
        handleCompletion(xpResult);
    };

    // Setup melee armor smithing completion callback
    smithingSkill.onSmithingMeleeArmorComplete = (armor, xpResult) => {
        handleCompletion(xpResult);
    };

    // Setup bulwark smithing completion callback
    smithingSkill.onSmithingBulwarkComplete = (bulwark, xpResult) => {
        handleCompletion(xpResult);
    };
}

if (typeof window !== 'undefined') {
    window.smithingUI = {
        toggleSmithing,
        toggleSmithingArrowheads,
        toggleSmithingSword,
        toggleSmithingDirk,
        toggleSmithingGreatsword,
        toggleSmithingMeleeArmor,
        toggleSmithingBulwark,
        upgradeTool,
        switchTab,
        switchMeleeTier
    };
}

export default {
    renderSmithingUI,
    toggleSmithing,
    toggleSmithingArrowheads,
    toggleSmithingSword,
    toggleSmithingDirk,
    toggleSmithingGreatsword,
    toggleSmithingMeleeArmor,
    toggleSmithingBulwark,
    upgradeTool,
    updateSmithingUI,
    initSmithingUI,
    switchTab,
    switchMeleeTier
};
